import React, { useState, useEffect, useCallback } from "react";
import { Paper, CircularProgress, Box, IconButton } from "@material-ui/core";
import { Timeline, Close } from "@material-ui/icons";
import { PrimaryText } from "components/Common/PrimaryText";
import { getBuildProgress } from "services/retention-service";

const BuildProgressButton = () => {
  const [progress, setProgress] = useState(null);
  const [visible, setVisible] = useState(false);
  const [userClosed, setUserClosed] = useState(false);

  const checkProgress = useCallback(async () => {
    try {
      const progressData = await getBuildProgress();

      if (progressData && !userClosed) {
        setProgress(progressData);
        setVisible(true);
        // Removed auto-hide - user will close manually
      } else if (!progressData) {
        // Reset userClosed when there's no progress data
        setUserClosed(false);
        setVisible(false);
        setProgress(null);
      }
    } catch (error) {
      console.error("Error checking progress:", error);
    }
  }, [userClosed]);

  useEffect(() => {
    // Check for progress on mount
    checkProgress();
  }, [checkProgress]);

  // Listen for build progress started event
  useEffect(() => {
    const handleBuildStarted = () => {
      // Add a small delay to ensure the backend has started
      setTimeout(checkProgress, 500);
    };

    window.addEventListener("buildProgressStarted", handleBuildStarted);
    return () =>
      window.removeEventListener("buildProgressStarted", handleBuildStarted);
  }, [checkProgress]);

  // Separate effect for polling - only when running
  useEffect(() => {
    if (progress?.status === "running") {
      const interval = setInterval(checkProgress, 2000);
      return () => clearInterval(interval);
    }
  }, [progress?.status, checkProgress]);

  if (!visible || !progress) {
    return null;
  }

  const getProgressPercentage = () => {
    if (progress.total === 0) return 0;
    return Math.round(
      ((progress.processed + progress.skipped) / progress.total) * 100
    );
  };

  const getStatusColor = () => {
    switch (progress.status) {
      case "running":
        return "#1976d2";
      case "completed":
        return "#388e3c";
      case "error":
        return "#d32f2f";
      default:
        return "#757575";
    }
  };

  const getStatusText = () => {
    switch (progress.status) {
      case "running":
        return `Building: ${
          progress.current || "Starting..."
        } (${getProgressPercentage()}%)`;
      case "completed":
        return `Complete! Processed: ${progress.processed}, Skipped: ${progress.skipped}`;
      case "error":
        return `Error: ${progress.error || "Unknown error"}`;
      default:
        return "Unknown status";
    }
  };

  return (
    <Box
      style={{
        position: "fixed",
        bottom: 30,
        right: 30,
        zIndex: 1000,
      }}
    >
      <Paper
        elevation={4}
        style={{
          padding: 20,
          backgroundColor: "white",
          minWidth: 350,
          display: "flex",
          alignItems: "center",
          gap: 12,
        }}
      >
        {progress.status === "running" ? (
          <CircularProgress size={28} style={{ color: getStatusColor() }} />
        ) : (
          <Timeline style={{ color: getStatusColor(), fontSize: 28 }} />
        )}

        <Box flex={1}>
          <PrimaryText
            fontSize={16}
            fontWeight={600}
            style={{ color: getStatusColor() }}
          >
            {progress.status === "running"
              ? "Building Historical Data"
              : progress.status === "completed"
              ? "Build Complete"
              : "Build Error"}
          </PrimaryText>
          <Box style={{ marginTop: 4 }}>
            <PrimaryText fontSize={14} style={{ color: "#666" }}>
              {getStatusText()}
            </PrimaryText>
          </Box>
          {progress.status === "running" && (
            <Box mt={1}>
              <Box
                style={{
                  height: 4,
                  backgroundColor: "#e0e0e0",
                  borderRadius: 2,
                  overflow: "hidden",
                }}
              >
                <Box
                  style={{
                    height: "100%",
                    width: `${getProgressPercentage()}%`,
                    backgroundColor: getStatusColor(),
                    transition: "width 0.3s ease",
                  }}
                />
              </Box>
              <PrimaryText
                fontSize={12}
                style={{ color: "#999", marginTop: 2 }}
              >
                {progress.processed + progress.skipped} of {progress.total} days
              </PrimaryText>
            </Box>
          )}
        </Box>
        <IconButton
          size="small"
          onClick={() => {
            setVisible(false);
            setProgress(null);
            setUserClosed(true);
          }}
          style={{ padding: 4 }}
        >
          <Close fontSize="small" />
        </IconButton>
      </Paper>
    </Box>
  );
};

export default BuildProgressButton;
